import { AnimatePresence } from "framer-motion";
import { z } from "zod";
import {
  AddPartnerProbeOverlay,
  addPartnerProbeSchema,
} from "./add-partner-probe/add-partner-probe-overlay";
import AddProbeOverlay, { addProbeSchema } from "./add-probe/add-probe-overlay";
import { AlertOverlay, alertSchema } from "./alerts/alert-overlay";
import {
  AddObjectExceptionOverlay,
  addObjectExceptionSchema,
} from "./control-monitor/overlays/add-object-exception-overlay";
import {
  AddPolicyExceptionOverlay,
  addPolicyExceptionSchema,
} from "./control-monitor/overlays/add-policy-exception-overlay";
import {
  ControlCategoryFailuresOverlay,
  controlCategoryFailuresSchema,
} from "./control-monitor/overlays/control-category-failures-overlay";
import {
  ControlEndpointsOverlay,
  controlEndpointsSchema,
} from "./control-monitor/overlays/control-endpoints-overlay";
import {
  ControlFailuresOverlay,
  controlFailuresSchema,
} from "./control-monitor/overlays/control-failures-overlay";
import {
  CriticalPoliciesOverlay,
  criticalPoliciesOverlaySchema,
} from "./control-monitor/overlays/critical-policies-overlay";
import {
  endpointsSchema,
  EndpointsOverlay,
} from "./control-monitor/overlays/endpoints-overlay";
import {
  ExceptionsOverlay,
  exceptionsSchema,
} from "./control-monitor/overlays/exceptions-overlay";
import {
  InboxesOverlay,
  inboxesSchema,
} from "./control-monitor/overlays/inboxes-overlay";
import {
  PoliciesOverlay,
  policiesSchema,
} from "./control-monitor/overlays/policies-overlay";
import {
  userSchema,
  UserOverlay,
} from "./control-monitor/overlays/user-overlay";
import {
  UsersOverlay,
  usersSchema,
} from "./control-monitor/overlays/users-overlay";
import EndpointOverlay, {
  endpointSchema,
} from "./endpoint-overlay/endpoint-overlay";
import {
  EventLogOverlay,
  eventLogSchema,
} from "./integrations/event-log-overlay";
import {
  IntegrationOverlay,
  integrationSchema,
} from "./integrations/integration-overlay";
import {
  MicrosoftAutomatedSetupOverlay,
  microsoftAutomatedSetupSchema,
} from "./integrations/microsoft-automated-setup-overlay";
import InviteUserOverlay, { inviteUserSchema } from "./invite-user/invite-user";
import {
  NewAccountConfirmation,
  newAccountSchema,
} from "./new-account-confirmation/new-account-confirmation";
import {
  DeleteScheduleModal,
  deleteScheduleSchema,
} from "./schedule/delete-schedule";
import ScheduleOverlay, {
  scheduleSchema,
} from "./schedule/schedule-overlay/schedule-overlay";
import ServiceUserOverlay, {
  serviceUserSchema,
} from "./service-user/service-user";
import TestOverlay, { testSchema } from "./test-overlay/test-overlay";
import {
  ThreatOverlay,
  threatOverlaySchema,
} from "./threat-overlay/threat-overlay";

export const overlaySchema = z
  .union([
    integrationSchema,
    eventLogSchema,
    addObjectExceptionSchema,
    addPolicyExceptionSchema,
    addProbeSchema,
    addPartnerProbeSchema,
    alertSchema,
    controlEndpointsSchema,
    controlFailuresSchema,
    controlCategoryFailuresSchema,
    criticalPoliciesOverlaySchema,
    policiesSchema,
    endpointsSchema,
    userSchema,
    usersSchema,
    inboxesSchema,
    inviteUserSchema,
    serviceUserSchema,
    scheduleSchema,
    endpointSchema,
    exceptionsSchema,
    testSchema,
    threatOverlaySchema,
    deleteScheduleSchema,
    newAccountSchema,
    microsoftAutomatedSetupSchema,
  ])
  .optional()
  .catch(undefined);

export type OverlaySchema = z.infer<typeof overlaySchema>;

export default function Overlays({ overlay }: { overlay: OverlaySchema }) {
  return <ShowOverlays overlay={overlay} />;
}

function ShowOverlays({ overlay }: { overlay: OverlaySchema }) {
  const [name, props] = overlay ?? [];

  return (
    <>
      <AnimatePresence>
        {name === "add-partner-probe" && <AddPartnerProbeOverlay />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "add-probe" && <AddProbeOverlay />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "add-object-exception" && (
          <AddObjectExceptionOverlay {...props} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {name === "add-policy-exception" && (
          <AddPolicyExceptionOverlay {...props} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {name === "alert" && <AlertOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "control-endpoints" && <ControlEndpointsOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "control-category-failures" && (
          <ControlCategoryFailuresOverlay {...props} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {name === "control-failures" && <ControlFailuresOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "critical-policies" && <CriticalPoliciesOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "policies" && <PoliciesOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "invite-user" && <InviteUserOverlay />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "service-user" && <ServiceUserOverlay />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "schedule" && <ScheduleOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "endpoints" && <EndpointsOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "users" && <UsersOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "user" && <UserOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "inboxes" && <InboxesOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "endpoint" && <EndpointOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "exceptions" && <ExceptionsOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "test" && <TestOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "threat" && <ThreatOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "deleteSchedule" && <DeleteScheduleModal {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "new-account" && <NewAccountConfirmation />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "event-log" && <EventLogOverlay />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "integration" && <IntegrationOverlay {...props} />}
      </AnimatePresence>
      <AnimatePresence>
        {name === "microsoft-automated-setup" && (
          <MicrosoftAutomatedSetupOverlay />
        )}
      </AnimatePresence>
    </>
  );
}
